import { hasDeepResearchPrefill } from '#pplx/frontend/ask-models/deepResearchSkill.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { isBrowserAgent } from '#pplx/frontend/ask-models/isBrowserAgent.js';
import { isSidecarSupportedMode, SearchMode, SearchModel, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { getCurrentSelection } from '#pplx/frontend/ask-query-submission/utils/clientContext.js';
import { createSubmitParams } from '#pplx/frontend/ask-query-submission/utils/createSubmitParams.js';
import { getLastBackendUUID } from '#pplx/frontend/ask-query-submission/utils/getLastBackendUUID.js';
import { getSidecarSafeSearchMode } from '#pplx/frontend/ask-query-submission/utils/getSidecarSafeSearchMode.js';
import { getSupportedBlockUseCases } from '#pplx/frontend/ask-query-submission/utils/getSupportedBlockUseCases.js';
import { getSearchPreferences } from '#pplx/frontend/ask-query-submission/utils/searchPreferencesUtils.js';
import { submitModeForSearchMode } from '#pplx/frontend/ask-query-submission/utils/submitMode.js';
import { useMaxCharsForClientContext } from '#pplx/frontend/ask-query-submission/utils/useMaxCharsForClientContext.js';
import { isCometBrowser, sendClientPayloadAsync, syncInlineAssistantParamsFromURL, } from '#pplx/frontend/comet/client/utils.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { getQueryWithQuote } from '#pplx/frontend/components/search/markdown/utils/quoteUtils.js';
import { WEB_VERSION } from '#pplx/frontend/components/utils/consts.js';
import { MessageHelper } from '#pplx/frontend/components/utils/MessageHelper.js';
import { getCookieComputerQueryCount, incrementCookieComputerQueryCount, incrementCookieDeepResearchQueryCount, incrementCookieLifetimeQueryCount, setQueryTimestamps, } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { useSingularAttribution } from '#pplx/frontend/growth/analytics-singular/SingularAttributionProviderShared.js';
import { useDeviceLocation } from '#pplx/frontend/locations/useDeviceLocation.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { startTimer } from '#pplx/frontend/metrics/browser.js';
import { useGetPageTimer } from '#pplx/frontend/metrics/providers/PerformanceProviderShared.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { CLIENT_SEARCH_PLACEHOLDER, createPlaceholderResult, } from '#pplx/frontend/stream/createPlaceholderResults.js';
import { PplxStreamError } from '#pplx/frontend/stream/errors.js';
import { useCreateBackwardsCompatibleStream } from '#pplx/frontend/stream/stream.js';
import { useCallback, useRef } from 'react';
export const useSubmitQuery = ({ cometMcpDisabledServers, cometState, compareModels, configuredModel, askDeepResearchModel, decrementFreeQueries, entropyBrowser, firstResult, freeQueries, getExperimentOverrides, getExperimentAssignments, getMergedPrefills, getSearchModeForSearchModel, initialConfiguredModel, incrementVisitorDailyQueryCount, injectSearchResults, isIncognito, isPersonalized, lastResult, limitedFreeTierExperience, maxAttachmentsLimit, openLimitedFreeTierGateModal, quote, reason, renderingPlace, rwToken, selectedSearchMode, setConfiguredModel, setCurrentStreamId, setQuote, setSubmissionQueryCount, shouldRunNavigationalSearch, skipSearchEnabled, sourcesFromContext, trackEvent, }) => {
    const createBackwardsCompatibleStream = useCreateBackwardsCompatibleStream();
    const { location: deviceLocation } = useDeviceLocation({ trackEvent });
    const getPageTimer = useGetPageTimer();
    const router = useRouter();
    const maxChars = useMaxCharsForClientContext();
    const { device: { isWindowsApp, isLinuxOS, isMacOS, isMobile }, } = useWebPlatform();
    const { sendActivatedComputerEventSingular, sendResurrectedActivationEventSingular } = useSingularAttribution();
    const { getDefaultModelForMode } = useSearchModels({ reason: 'submit-query-asi-default' });
    // The submit handler is invoked from user events. Reading its inputs through a
    // render-assigned ref keeps the returned function stable while an answer
    // streams (`lastResult` changes on every token), so context consumers do not
    // re-render per token.
    const latestInputs = useRef({
        cometMcpDisabledServers,
        cometState,
        compareModels,
        configuredModel,
        askDeepResearchModel,
        createBackwardsCompatibleStream,
        decrementFreeQueries,
        deviceLocation,
        entropyBrowser,
        firstResult,
        freeQueries,
        getDefaultModelForMode,
        getExperimentOverrides,
        getExperimentAssignments,
        getMergedPrefills,
        getPageTimer,
        getSearchModeForSearchModel,
        incrementVisitorDailyQueryCount,
        initialConfiguredModel,
        injectSearchResults,
        isIncognito,
        isMobile,
        isPersonalized,
        isWindowsApp,
        isLinuxOS,
        isMacOS,
        lastResult,
        limitedFreeTierExperience,
        maxAttachmentsLimit,
        maxChars,
        openLimitedFreeTierGateModal,
        quote,
        reason,
        renderingPlace,
        router,
        rwToken,
        selectedSearchMode,
        sendActivatedComputerEventSingular,
        sendResurrectedActivationEventSingular,
        setConfiguredModel,
        setCurrentStreamId,
        setQuote,
        setSubmissionQueryCount,
        shouldRunNavigationalSearch,
        skipSearchEnabled,
        sourcesFromContext,
        trackEvent,
    });
    latestInputs.current = {
        cometMcpDisabledServers,
        cometState,
        compareModels,
        configuredModel,
        askDeepResearchModel,
        createBackwardsCompatibleStream,
        decrementFreeQueries,
        deviceLocation,
        entropyBrowser,
        firstResult,
        freeQueries,
        getDefaultModelForMode,
        getExperimentOverrides,
        getExperimentAssignments,
        getMergedPrefills,
        getPageTimer,
        getSearchModeForSearchModel,
        incrementVisitorDailyQueryCount,
        initialConfiguredModel,
        injectSearchResults,
        isIncognito,
        isMobile,
        isPersonalized,
        isWindowsApp,
        isLinuxOS,
        isMacOS,
        lastResult,
        limitedFreeTierExperience,
        maxAttachmentsLimit,
        maxChars,
        openLimitedFreeTierGateModal,
        quote,
        reason,
        renderingPlace,
        router,
        rwToken,
        selectedSearchMode,
        sendActivatedComputerEventSingular,
        sendResurrectedActivationEventSingular,
        setConfiguredModel,
        setCurrentStreamId,
        setQuote,
        setSubmissionQueryCount,
        shouldRunNavigationalSearch,
        skipSearchEnabled,
        sourcesFromContext,
        trackEvent,
    };
    return useCallback(({ analyticsPolicy, alwaysSearchOverride, attachments, branchMode, browserAgentAllowOnceFromToggle, canonicalPageContext, collection, compareModelPreferencesOverride, deviceLocation: _deviceLocation, disableWidgets, dslQuery, modeConfig, enableLearnAboutMe, existingEntryUUID, existingFrontendContextUUID, existingUuid, forceDisableBrowserAgent, forceEnableBrowserAgent, fork, frontendUUID = safeRandomUUID(), inDomain, incognitoOverride, inPage, inPageOverride, isLocalBrowserAvailable, isWebBrowserExtensionAvailable, isNavSuggestionsDisabled, isRelatedQuery, policyApprovalDecision, isSponsored, lastBackendUUIDOverride, parentContextUUIDOverride, localWorkspaceDirectories, localFileGrants, mentions = [], modelPreferenceOverride, newFrontendContextUUID, onFirstMessageReceived: onFirstMessageReceivedCallback, onThreadSlugReceived, overrideNoSearch, prefills, promptSource, queryDisplayMetadata, querySource, rawQuery, redoSearch, refinementFiltersMetadata, relatedQueryUUID, searchModeOverride, shouldRedirectToBackendUUID, shouldSetCurrentStreamId = true, shouldUsePageStartTime, sourcesOverride, submitFromArticleOverride, targetThreadAccessLevelOverride, timeFromFirstType, utmSource, workflowConfig, workflowInstructions, workflowKey, sourceMetadata, }) => {
        const { cometMcpDisabledServers, cometState, compareModels, configuredModel, askDeepResearchModel, createBackwardsCompatibleStream, decrementFreeQueries, deviceLocation, entropyBrowser, firstResult, freeQueries, getDefaultModelForMode, getExperimentOverrides, getExperimentAssignments, getMergedPrefills, getPageTimer, getSearchModeForSearchModel, incrementVisitorDailyQueryCount, initialConfiguredModel, injectSearchResults, isIncognito, isMobile, isPersonalized, isWindowsApp, isLinuxOS, isMacOS, lastResult, limitedFreeTierExperience, maxAttachmentsLimit, maxChars, openLimitedFreeTierGateModal, quote, reason, renderingPlace, router, rwToken, selectedSearchMode, sendActivatedComputerEventSingular, sendResurrectedActivationEventSingular, setConfiguredModel, setCurrentStreamId, setQuote, setSubmissionQueryCount, shouldRunNavigationalSearch, skipSearchEnabled, sourcesFromContext, trackEvent, } = latestInputs.current;
        const effectiveSearchMode = searchModeOverride ?? selectedSearchMode;
        const isAskDeepResearchSubmission = !!askDeepResearchModel &&
            effectiveSearchMode === SearchMode.ASI &&
            !branchMode &&
            !fork &&
            !existingEntryUUID &&
            !existingFrontendContextUUID &&
            !lastBackendUUIDOverride &&
            (!!newFrontendContextUUID || !lastResult) &&
            hasDeepResearchPrefill(prefills);
        const isBranchModeEnabled = !!branchMode;
        const query = rawQuery.trim();
        if (query.length === 0 && !quote) {
            logger.error('Submit failed: empty query');
            return false;
        }
        const freeQueriesRemaining = freeQueries?.remaining_detail.kind === 'exact'
            ? (freeQueries.remaining_detail.remaining ?? null)
            : null;
        if (limitedFreeTierExperience !== 'control' &&
            freeQueriesRemaining !== null &&
            freeQueriesRemaining < 1) {
            openLimitedFreeTierGateModal(limitedFreeTierExperience === 'free-trial' ? 'free-trial' : 'upgrade');
            return false;
        }
        if (forceDisableBrowserAgent && isBrowserAgent(configuredModel)) {
            setConfiguredModel(initialConfiguredModel);
        }
        const attachedTabs = mentions
            .filter((mention) => mention.type === 'tab')
            .map((item) => {
            return {
                id: Number(item.id),
                url: item.url,
            };
        });
        const forceNew = !!newFrontendContextUUID;
        const newAttachments = [...new Set([...(attachments ?? [])])].slice(0, maxAttachmentsLimit);
        const shouldAttachCurrentPage = !!import.meta.env.VITE_IS_SIDECAR_BUILD ||
            !!import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD ||
            !!import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD ||
            !!import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD;
        const shouldUseClientPayload = isCometBrowser() && (shouldAttachCurrentPage || Boolean(attachedTabs.length));
        const clientPayloadUUID = shouldUseClientPayload ? safeRandomUUID() : undefined;
        let modelPreferenceSubmit = configuredModel;
        if (isAskDeepResearchSubmission) {
            // The backend pins Deep Research turns to the arm's model; send the covered default.
            modelPreferenceSubmit = SearchModel.ASI;
        }
        else if (modelPreferenceOverride) {
            modelPreferenceSubmit = modelPreferenceOverride;
        }
        else if (effectiveSearchMode === SearchMode.ASI &&
            modelPreferenceSubmit === SearchModel.ASI) {
            // Generic pplx_asi can be latched before models config loads; resolve to
            // the loaded ASI default (no-op if it's already generic).
            modelPreferenceSubmit = getDefaultModelForMode(SearchMode.ASI);
        }
        const modeConfigForSubmit = isAskDeepResearchSubmission ? undefined : modeConfig;
        const isSidecarBuild = !!import.meta.env.VITE_IS_SIDECAR_BUILD;
        const sidecarSafeSearchMode = getSidecarSafeSearchMode({
            isSidecarBuild,
            searchMode: effectiveSearchMode,
        });
        if (isSidecarBuild) {
            if (sidecarSafeSearchMode !== effectiveSearchMode) {
                logger.warn('Sidecar coerced unsupported search mode', {
                    requested: effectiveSearchMode,
                    coerced: sidecarSafeSearchMode,
                });
            }
            const modelMode = getSearchModeForSearchModel(modelPreferenceSubmit);
            if (!isSidecarSupportedMode(modelMode)) {
                logger.warn('Sidecar reset unsupported model_preference', {
                    modelPreference: modelPreferenceSubmit,
                    derivedMode: modelMode,
                });
                modelPreferenceSubmit = SearchModel.DEFAULT;
            }
        }
        const isAsiLikeMode = sidecarSafeSearchMode === SearchMode.ASI;
        const mode = submitModeForSearchMode(sidecarSafeSearchMode);
        const isModelCouncilMode = sidecarSafeSearchMode === SearchMode.AGENTIC_RESEARCH;
        const compareModelPreferencesForSubmit = compareModelPreferencesOverride && compareModelPreferencesOverride.length > 0
            ? [...compareModelPreferencesOverride]
            : isModelCouncilMode && compareModels && compareModels.length > 0
                ? [...compareModels]
                : undefined;
        const lastBackendUUID = lastBackendUUIDOverride ??
            getLastBackendUUID(forceNew, fork ?? false, firstResult?.backend_uuid === existingEntryUUID, submitFromArticleOverride ?? null, lastResult?.backend_uuid ?? null);
        incrementCookieLifetimeQueryCount();
        incrementVisitorDailyQueryCount();
        const newFreeQueriesRemaining = decrementFreeQueries();
        if (limitedFreeTierExperience !== 'control' &&
            newFreeQueriesRemaining !== null &&
            newFreeQueriesRemaining < 1) {
            openLimitedFreeTierGateModal(limitedFreeTierExperience === 'free-trial' ? 'free-trial' : 'upgrade');
        }
        setSubmissionQueryCount((prev) => prev + 1);
        setQueryTimestamps(() => {
            trackEvent('resurrected activated');
            sendResurrectedActivationEventSingular();
        });
        if (getSearchModeForSearchModel(modelPreferenceSubmit) === SearchMode.RESEARCH) {
            incrementCookieDeepResearchQueryCount();
        }
        else if (isAsiLikeMode) {
            incrementCookieComputerQueryCount();
            if (getCookieComputerQueryCount() === 1) {
                sendActivatedComputerEventSingular();
            }
        }
        const isFollowup = !!lastBackendUUID;
        const isInSameThread = firstResult?.frontend_context_uuid === existingFrontendContextUUID;
        const searchPreferences = getSearchPreferences({
            isFollowup,
            isInSameThread,
            sourcesOverride,
            firstResultSources: firstResult?.sources?.sources,
            defaultSources: [...(sourcesFromContext ?? [])],
        });
        const { finalSources } = searchPreferences;
        let mcpTools;
        if (cometState?.mcpTools) {
            mcpTools = Object.entries(cometState.mcpTools)
                .filter(([serverName]) => !cometMcpDisabledServers?.has(serverName))
                .flatMap(([serverName, tools]) => {
                const settings = cometState.mcpToolsSettings?.[serverName] ?? {};
                return tools.filter((tool) => {
                    const name = tool.tool?.name ?? tool.id ?? '';
                    const toolSettings = settings[name];
                    return toolSettings ? toolSettings.enabled !== false : true;
                });
            });
        }
        const selection = getCurrentSelection({
            inPage: (inPageOverride ?? inPage),
        });
        const selectedText = selection || quote;
        const finalQuery = (selectedText ? getQueryWithQuote(rawQuery, selectedText) : rawQuery).trim();
        if (!finalQuery) {
            logger.error('Submit failed: empty query');
            return false;
        }
        const isFirstEntry = existingEntryUUID == firstResult?.backend_uuid || !lastBackendUUID;
        const shouldUseNavV3CacheKey = !isCometBrowser() &&
            shouldRunNavigationalSearch({
                query: finalQuery,
                isFollowup,
                attachmentsLength: newAttachments.length,
                sourcesLength: finalSources?.length ?? 0,
                modelPreference: modelPreferenceSubmit ?? undefined,
            });
        const nextResult = createPlaceholderResult({
            rawQuery: finalQuery,
            dslQuery,
            fork,
            existingEntryUUID,
            existingUuid,
            collection,
            targetThreadAccessLevelOverride,
            isRelatedQuery,
            newFrontendContextUUID,
            existingFrontendContextUUID,
            submitFromArticleOverride,
            isNavSuggestionsDisabled,
            isCopilot: true,
            queryMode: mode,
            sourcesFromContext: [...(sourcesFromContext ?? [])],
            modelPreferenceSubmit,
            modeConfig: modeConfigForSubmit,
            newAttachments,
            firstResultFrontendContextUUID: firstResult?.frontend_context_uuid,
            parentContextUUID: forceNew
                ? undefined
                : (parentContextUUIDOverride ?? firstResult?.context_uuid),
            isPersonalized,
            forceNew,
            querySource,
            lastResultBackendUUID: lastResult?.backend_uuid,
            finalSources,
            uuid: frontendUUID,
            // For the branched thread's placeholder parent_info, prefer the SSE-fresh
            // title/slug carried via branchMode over firstResult, which is a frozen REST
            // snapshot taken at page load and never refreshed by SSE. ASI thread titles
            // are generated async by an agent webhook, so firstResult.thread_title is
            // often empty when the SSE-merged value (used for displayTitle) is not.
            // Backend parent_info from the stream still wins once it arrives
            // (see stream.tsx merge: message.parent_info ?? existingEntry?.parent_info).
            threadMetadataTitle: branchMode?.sourceThreadTitle || firstResult?.thread_title,
            threadMetadataThreadUrlSlug: branchMode?.sourceThreadUrlSlug || firstResult?.thread_url_slug,
            threadMetadataMode: branchMode?.sourceThreadMode || firstResult?.mode,
            queryDisplayMetadata,
        });
        let inlineAssistantMode;
        if (import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD && entropyBrowser) {
            syncInlineAssistantParamsFromURL({ cometAdapter: entropyBrowser });
            const updatedState = entropyBrowser?.getStore().getState().inlineAssistantParams;
            inlineAssistantMode = updatedState.isWritingMode
                ? 'writing'
                : 'reading';
        }
        const params = createSubmitParams({
            isAskDeepResearch: isAskDeepResearchSubmission,
            redoSearch,
            collection,
            dslQuery,
            isRelatedQuery,
            isSponsored,
            relatedQueryUUID,
            inDomain,
            inPage,
            newFrontendContextUUID,
            promptSource,
            querySource,
            timeFromFirstType,
            utmSource,
            disableWidgets,
            isNavSuggestionsDisabled,
            isWindowsApp,
            isLinuxOS,
            isMacOS,
            isIncognito: incognitoOverride ?? isIncognito,
            modelPreferenceSubmit,
            modeConfig: modeConfigForSubmit,
            compareModelPreferences: compareModelPreferencesForSubmit,
            clientPayloadUUID,
            lastBackendUUID,
            branchMode: isBranchModeEnabled,
            deviceLocation: _deviceLocation ?? deviceLocation,
            newAttachments,
            threadMetadataRwToken: rwToken,
            forceNew,
            finalSources,
            mode,
            frontendUUID,
            entropyBrowser,
            existingEntryUUID,
            mentions,
            queryDisplayMetadata,
            skipSearchEnabled,
            sidecarTabId: cometState?.sidecarSourceTab.tabId,
            sidecarTitle: cometState?.sidecarSourceTab.title,
            sidecarUrl: cometState?.sidecarSourceTab.url,
            sidecarSecondaryTabId: cometState?.sidecarSourceTab.secondaryTab?.tabId,
            sidecarSecondaryUrl: cometState?.sidecarSourceTab.secondaryTab?.url,
            sidecarSecondaryTitle: cometState?.sidecarSourceTab.secondaryTab?.title,
            isFirstEntry,
            alwaysSearchOverride,
            overrideNoSearch,
            mcpTools,
            refinementFiltersMetadata,
            browserAgentAllowOnceFromToggle,
            forceEnableBrowserAgent: forceEnableBrowserAgent || sidecarSafeSearchMode === SearchMode.BROWSER_AGENT,
            forceDisableBrowserAgent,
            enableLearnAboutMe,
            localWorkspaceDirectories,
            localFileGrants,
            canonicalPageContext,
            inlineMode: inlineAssistantMode,
            isMobile,
            shouldUseNavV3CacheKey,
            supportedBlockUseCases: getSupportedBlockUseCases({ isAsiLikeMode }),
            isLocalBrowserAvailable,
            isWebBrowserExtensionAvailable,
            policyApprovalDecision,
            targetThreadAccessLevelOverride,
            getExperimentOverrides,
            getExperimentAssignments,
            renderingPlace,
            prefills: getMergedPrefills({ prefills, isAsiLikeMode }),
            workflowInstructions,
            workflowConfig,
            workflowKey,
            sourceMetadata,
        });
        if (newFrontendContextUUID) {
            injectSearchResults(finalQuery, params, params.frontend_uuid);
        }
        setQuote(null);
        const finalParams = {
            params: {
                ...params,
                version: WEB_VERSION,
            },
            query_str: finalQuery,
        };
        function onFirstMessageReceived(message) {
            if (onThreadSlugReceived) {
                if (MessageHelper.isStatusFailed(message)) {
                    throw new PplxStreamError('STREAM_FAILED_FIRST_CHUNK_ERROR', {
                        message: 'failed status on first message',
                        details: {
                            request_id: message.frontend_uuid ?? 'unknown',
                            backend_uuid: message.backend_uuid,
                        },
                    });
                }
                else if (!message.thread_url_slug) {
                    throw new PplxStreamError('STREAM_FAILED_FIRST_CHUNK_ERROR', {
                        message: 'thread_url_slug is required on first message',
                        details: {
                            request_id: message.frontend_uuid ?? 'unknown',
                            backend_uuid: message.backend_uuid,
                        },
                    });
                }
                onThreadSlugReceived(message.thread_url_slug);
            }
            if (onFirstMessageReceivedCallback && message.thread_url_slug) {
                onFirstMessageReceivedCallback({
                    slug: message.thread_url_slug,
                    context_uuid: message.context_uuid ?? null,
                    backend_uuid: message.backend_uuid ?? null,
                });
            }
            if (shouldRedirectToBackendUUID) {
                // The #0 hash tells Computer to start on the branched parent card instead
                // of auto-scrolling to the newly created child entry.
                const redirectPath = `${isAsiLikeMode ? '/computer/tasks' : '/search'}/${message.thread_url_slug}${isBranchModeEnabled && isAsiLikeMode ? '#0' : ''}`;
                router.push(redirectPath);
            }
        }
        const stream = createBackwardsCompatibleStream(finalParams, {
            analyticsPolicy,
            isNewThread: forceNew && shouldSetCurrentStreamId,
            onFirstMessageReceived,
            placeholderChunk: {
                ...nextResult,
                backend_uuid: existingEntryUUID || CLIENT_SEARCH_PLACEHOLDER,
            },
            timer: shouldUsePageStartTime
                ? getPageTimer()
                : startTimer(undefined, {
                    nowFromTimeOrigin: performance.now(),
                }),
            reason,
        });
        const streamId = stream.id.description;
        if (shouldUseClientPayload && clientPayloadUUID && entropyBrowser && cometState) {
            sendClientPayloadAsync({
                clientPayloadCacheKey: clientPayloadUUID,
                cometBrowser: entropyBrowser,
                maxChars,
                attachedTabs,
                includeSidecar: shouldAttachCurrentPage,
                reason,
                cometState,
                requestId: streamId,
            });
        }
        if (shouldSetCurrentStreamId) {
            setCurrentStreamId(streamId);
        }
        return true;
    }, [latestInputs]);
};
//# sourceMappingURL=useSubmitQuery.js.map